import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../utils/axiosConfig';
import LanguageSwitcher from './LanguageSwitcher';
import CookieConsent from './CookieConsent';
import { currentPublicLang, applyPublicSeo } from '../utils/publicLocale';
import cffaLogo from '../assets/cffa-logo.png';
import './PublicSite.css';

// Persisted cookie choice for the public site.
//   'accepted' → optional cookies (Google reCAPTCHA) allowed
//   'declined' → optional cookies refused
//   null       → no decision yet (banner shown)
const CONSENT_KEY = 'cffaCookieConsent';
const readConsent = () => {
    try {
        return localStorage.getItem(CONSENT_KEY);
    } catch (err) {
        return null;
    }
};

// Google Analytics 4 — an optional (non-essential) tracker. It is loaded
// dynamically only after the visitor accepts optional cookies, never on page
// load, so no Google Analytics cookies are set without consent.
const GA4_ID = 'G-3ZL9MMDRMW';

// Best-effort removal of Google Analytics cookies (_ga, _ga_*, _gid) when a
// visitor withdraws consent.
const clearGaCookies = () => {
    try {
        document.cookie.split(';').forEach((entry) => {
            const name = entry.split('=')[0].trim();
            if (name.startsWith('_ga') || name === '_gid') {
                const base = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
                document.cookie = base;
                document.cookie = `${base}; domain=.${window.location.hostname}`;
            }
        });
    } catch (err) {
        /* nothing we can do if cookie access is blocked */
    }
};

// Location names/addresses are proper nouns — intentionally not translated.
const LOCATIONS = [
    { brand: 'aw', name: 'A&W – Laval', addr: '1695 Bd Curé-Labelle, Laval, QC H7V 2W6' },
    { brand: 'aw', name: 'A&W – Montréal-Ouest', addr: '7235 Rue Saint-Jacques, Montréal-Ouest, QC H4B 1V3' },
    { brand: 'aw', name: 'A&W – Anjou', addr: '7600 Rue Béclard, Anjou, QC H1J 2X7' },
    { brand: 'aw', name: 'A&W – Montréal (Maurice-Duplessis)', addr: '8000 Bd Maurice-Duplessis, Montréal, QC H1E 2Y9' },
    { brand: 'aw', name: 'A&W – Mount Royal (Foire alimentaire)', addr: 'Foire alimentaire, 2305 Chem. Rockland, Mount Royal, QC H3P 3E9' },
    { brand: 'aw', name: 'A&W – Carrefour Angrignon, LaSalle', addr: 'Carrefour Angrignon, 7077 Newman Boulevard, LaSalle, QC H8N 1X1' },
    { brand: 'aw', name: 'A&W – Sherbrooke St W, Montreal', addr: '5150 Sherbrooke St W, Montreal, QC H4A 1T4' },
    { brand: 'aw', name: 'A&W – Newman Blvd, LaSalle', addr: '7610 Newman Blvd, LaSalle, QC H8N 1B6' },
    { brand: 'aw', name: 'A&W – Premium Outlets Montréal, Mirabel', addr: 'Premium Outlets Montréal, 19001 Chemin Notre Dame, Mirabel, QC J7J 0R6' },
    { brand: 'aw', name: 'A&W – Pierrefonds-Roxboro', addr: '4520 Saint-Charles Blvd, Pierrefonds-Roxboro, QC H9H 3C8' },
    { brand: 'aw', name: 'A&W – Rivière-Beaudette', addr: '615 Rue Principale, Rivière-Beaudette, QC J0P 1R0' },
    { brand: 'aw', name: 'A&W – Dorval', addr: '975 boul. Roméo-Vachon N, Local T2705, Dorval, QC H4Y 1H1' },
    { brand: 'calienteh', name: 'Calient-eh – Sherbrooke St W, Montreal', addr: '5152 Sherbrooke St W, Montreal, QC H4A 1T4' },
    { brand: 'calienteh', name: 'Calient-eh – Newman Blvd, LaSalle', addr: '7077 Newman Blvd, LaSalle, QC H8N 1X1' },
];

const POSITION_KEYS = ['crew', 'cashier', 'cook', 'shiftSupervisor', 'assistantManager', 'manager', 'other'];
const CAPITAL_KEYS = ['r1', 'r2', 'r3', 'r4', 'r5'];

const AwIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="#fffaf3" strokeWidth="1.6">
        <path d="M4 10c0-3 3-6 8-6s8 3 8 6" strokeLinecap="round" />
        <path d="M3 10h18l-1.5 9h-15z" strokeLinejoin="round" />
        <path d="M8 13v3M12 13v3M16 13v3" strokeLinecap="round" />
    </svg>
);

const ChiliIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="#fffaf3" strokeWidth="1.6">
        <path d="M14 3c1.5 1 1 3-.5 3.5" strokeLinecap="round" />
        <path d="M13 6c4 0 7 3 7 7 0 5-4 8-8 8-3.5 0-6-2.5-6-5.5C6 10 10 6 13 6z" strokeLinejoin="round" />
    </svg>
);

const BrandLogo = ({ alt }) => (
    <img className="brand-logo" src={cffaLogo} alt={alt} />
);

const SCALLOP_PATH = 'M0,44 L0,20 Q15,44 30,20 Q45,-4 60,20 Q75,44 90,20 Q105,-4 120,20 Q135,44 150,20 Q165,-4 180,20 Q195,44 210,20 Q225,-4 240,20 Q255,44 270,20 Q285,-4 300,20 Q315,44 330,20 Q345,-4 360,20 Q375,44 390,20 Q405,-4 420,20 Q435,44 450,20 Q465,-4 480,20 Q495,44 510,20 Q525,-4 540,20 Q555,44 570,20 Q585,-4 600,20 Q615,44 630,20 Q645,-4 660,20 Q675,44 690,20 Q705,-4 720,20 Q735,44 750,20 Q765,-4 780,20 Q795,44 810,20 Q825,-4 840,20 Q855,44 870,20 Q885,-4 900,20 Q915,44 930,20 Q945,-4 960,20 Q975,44 990,20 Q1005,-4 1020,20 Q1035,44 1050,20 Q1065,-4 1080,20 Q1095,44 1110,20 Q1125,-4 1140,20 Q1155,44 1170,20 Q1185,-4 1200,20 L1200,44 Z';

const EMPTY_JOB = { name: '', email: '', phone: '', location: '', position: '', type: '', website: '' };
const EMPTY_IDEA = { name: '', email: '', phone: '', idea: '', capital: '', experience: '', website: '' };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PublicSite = () => {
    const { t, i18n } = useTranslation();
    const routerLocation = useLocation();

    const [navOpen, setNavOpen] = useState(false);
    const [filter, setFilter] = useState('all');

    const [job, setJob] = useState(EMPTY_JOB);
    const [jobStatus, setJobStatus] = useState(null);
    const [jobSending, setJobSending] = useState(false);
    const [idea, setIdea] = useState(EMPTY_IDEA);
    const [ideaStatus, setIdeaStatus] = useState(null);
    const [ideaSending, setIdeaSending] = useState(false);

    // Point-of-collection consent (required to submit). Hardcoded, not CMS-editable.
    const [jobConsent, setJobConsent] = useState(false);
    const [ideaConsent, setIdeaConsent] = useState(false);

    // Active language is derived from the URL (/en... = English, else French),
    // not from localStorage, so each clean path renders a fixed locale.
    const language = currentPublicLang();

    // Keep i18next in sync with the URL locale so every t()/ct() string renders
    // in the right language, without clobbering the stored preference that the
    // logged-in app uses (restored immediately after the change).
    useEffect(() => {
        if (i18n.language === language) return;
        let stored = null;
        try { stored = localStorage.getItem('i18nextLng'); } catch (e) { /* blocked */ }
        i18n.changeLanguage(language);
        try {
            if (stored) localStorage.setItem('i18nextLng', stored);
            else localStorage.removeItem('i18nextLng');
        } catch (e) { /* blocked */ }
    }, [language, i18n]);

    // --- Admin-managed content overrides & locations (fall back to built-ins) ---
    const [overrides, setOverrides] = useState({});
    const [apiLocations, setApiLocations] = useState(null);

    useEffect(() => {
        let cancelled = false;
        axios.get('/api/public-content')
            .then((res) => {
                if (!cancelled && res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
                    setOverrides(res.data);
                }
            })
            .catch(() => { /* keep built-in copy if overrides can't load */ });
        axios.get('/api/locations')
            .then((res) => {
                if (!cancelled && Array.isArray(res.data) && res.data.length > 0) {
                    setApiLocations(res.data);
                }
            })
            .catch(() => { /* keep hardcoded locations if API unavailable */ });
        return () => { cancelled = true; };
    }, []);

    // --- CMS draft preview mode: opened from the admin content editor with
    // unsaved changes stashed in localStorage. Never affects normal visitors
    // (requires the cmsPreview flag in the URL). ---
    const isPreview = window.location.hash.includes('cmsPreview=1');
    const previewOverrides = useMemo(() => {
        if (!isPreview) return null;
        try {
            const raw = localStorage.getItem('cffaCmsPreview');
            return raw ? (JSON.parse(raw).overrides || null) : null;
        } catch (err) {
            return null;
        }
    }, [isPreview]);

    // Content helper: draft preview first (if any), then saved admin override,
    // else built-in translation.
    const ct = (key) => (previewOverrides && previewOverrides[key]?.[language])
        || overrides[key]?.[language]
        || t(key);

    const locations = apiLocations || LOCATIONS;

    // --- SEO: keep title / meta description / social tags in sync with the
    // visitor's language (static defaults live in public/index.html, French). ---
    useEffect(() => {
        const setMeta = (selector, content) => {
            const el = document.head.querySelector(selector);
            if (el && content) el.setAttribute('content', content);
        };
        const title = ct('public.seo.title');
        const description = ct('public.seo.description');
        const ogDescription = ct('public.seo.ogDescription');

        document.title = title;
        setMeta('meta[name="description"]', description);
        setMeta('meta[property="og:title"]', title);
        setMeta('meta[property="og:description"]', ogDescription);
        setMeta('meta[name="twitter:title"]', title);
        setMeta('meta[name="twitter:description"]', ogDescription);
        // <html lang>, canonical, hreflang (fr/en/x-default), og:url + og:locale
        // for the home page ("/") in the active locale.
        applyPublicSeo(language, '/');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, overrides, previewOverrides]);

    // Dynamic filter counts — computed from the live location list so the
    // client never has to hand-edit numbers when adding/removing locations.
    // stripCount removes any trailing "(12)" / "14" left in older saved text.
    const stripCount = (s) => String(s).replace(/\s*\(?\d+\)?\s*$/, '');
    const brandCount = (brand) => locations.filter((l) => l.brand === brand).length;

    // --- Cookie consent (gates non-essential trackers, i.e. Google reCAPTCHA) ---
    const [consent, setConsent] = useState(readConsent);          // 'accepted' | 'declined' | null
    const [bannerOpen, setBannerOpen] = useState(() => readConsent() == null);

    const recordConsent = (value) => {
        try {
            localStorage.setItem(CONSENT_KEY, value);
        } catch (err) {
            /* private mode / storage blocked — keep choice for this session only */
        }
        if (value === 'declined') {
            // Withdraw consent: stop GA from sending any further hits (honoured
            // even if it was already loaded this session) and drop its cookies.
            window[`ga-disable-${GA4_ID}`] = true;
            clearGaCookies();
        } else if (value === 'accepted') {
            window[`ga-disable-${GA4_ID}`] = false;
        }
        setConsent(value);
        setBannerOpen(false);
    };
    const acceptCookies = () => recordConsent('accepted');
    const declineCookies = () => recordConsent('declined');
    const dismissBanner = () => setBannerOpen(false); // closed for this visit, no consent recorded
    const openCookieSettings = () => setBannerOpen(true); // let visitors revisit their choice

    // Load Google Analytics only once the visitor has accepted optional cookies.
    useEffect(() => {
        if (consent !== 'accepted' || window.__ga4Loaded) return;
        window.__ga4Loaded = true;

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
        document.head.appendChild(script);

        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag() { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        window.gtag('config', GA4_ID);
    }, [consent]);

    // --- reCAPTCHA (enabled/configured by admin in dashboard settings) ---
    const [recaptcha, setRecaptcha] = useState({ enabled: false, siteKey: '' });
    const jobCaptchaRef = useRef(null);
    const ideaCaptchaRef = useRef(null);
    const jobWidgetId = useRef(null);
    const ideaWidgetId = useRef(null);

    useEffect(() => {
        let cancelled = false;
        axios.get('/api/submissions/config')
            .then((res) => {
                if (!cancelled) {
                    setRecaptcha({
                        enabled: Boolean(res.data.recaptchaEnabled),
                        siteKey: res.data.recaptchaSiteKey || '',
                    });
                }
            })
            .catch(() => { /* captcha stays disabled if config can't load */ });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        // Only load Google's reCAPTCHA once the visitor has accepted optional
        // cookies. Without consent the script (and its cookies) never loads.
        if (!recaptcha.enabled || !recaptcha.siteKey || consent !== 'accepted') return;

        const renderWidgets = () => {
            if (!window.grecaptcha || !window.grecaptcha.render) return;
            if (jobCaptchaRef.current && jobWidgetId.current === null) {
                jobWidgetId.current = window.grecaptcha.render(jobCaptchaRef.current, { sitekey: recaptcha.siteKey });
            }
            if (ideaCaptchaRef.current && ideaWidgetId.current === null) {
                ideaWidgetId.current = window.grecaptcha.render(ideaCaptchaRef.current, { sitekey: recaptcha.siteKey });
            }
        };

        if (window.grecaptcha && window.grecaptcha.render) {
            renderWidgets();
            return;
        }
        window.onRecaptchaLoad = renderWidgets;
        if (!document.getElementById('recaptcha-script')) {
            const script = document.createElement('script');
            script.id = 'recaptcha-script';
            script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit&hl=${language}`;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }
    }, [recaptcha, language, consent]);

    const getCaptchaToken = (widgetIdRef) => {
        if (!recaptcha.enabled) return null; // captcha off → nothing required
        if (!window.grecaptcha || widgetIdRef.current === null) return '';
        return window.grecaptcha.getResponse(widgetIdRef.current);
    };

    const resetCaptcha = (widgetIdRef) => {
        if (window.grecaptcha && widgetIdRef.current !== null) {
            window.grecaptcha.reset(widgetIdRef.current);
        }
    };

    // Handle redirects from old routes (/about, /locations, ...) that pass a
    // target section id via location state.
    useEffect(() => {
        const target = routerLocation.state && routerLocation.state.scrollTo;
        if (target) {
            const timer = setTimeout(() => {
                const el = document.getElementById(target);
                if (el) el.scrollIntoView({ behavior: 'smooth' });
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [routerLocation.state]);

    // HashRouter owns the URL hash, so anchor navigation is done in JS.
    const scrollTo = (id) => (event) => {
        event.preventDefault();
        setNavOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    const showBrand = (brand) => (event) => {
        event.preventDefault();
        setFilter(brand);
        const el = document.getElementById('locations');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    const handleJobChange = (field) => (event) => {
        setJob((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleIdeaChange = (field) => (event) => {
        setIdea((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleJobSubmit = async (event) => {
        event.preventDefault();
        if (!job.name.trim() || !job.email.trim() || !job.location || !job.position || !job.type) {
            setJobStatus({ type: 'error', reason: 'required' });
            return;
        }
        if (!EMAIL_RE.test(job.email.trim())) {
            setJobStatus({ type: 'error', reason: 'email' });
            return;
        }
        if (!jobConsent) {
            setJobStatus({ type: 'error', reason: 'consent' });
            return;
        }
        // reCAPTCHA only applies to visitors who accepted optional cookies.
        // Those who declined submit with honeypot + rate-limit protection.
        let jobToken;
        if (recaptcha.enabled && consent === 'accepted') {
            jobToken = getCaptchaToken(jobWidgetId);
            if (jobToken === '') {
                setJobStatus({ type: 'error', reason: 'captcha' });
                return;
            }
        }
        setJobSending(true);
        try {
            await axios.post('/api/submissions', {
                type: 'job',
                name: job.name.trim(),
                email: job.email.trim(),
                phone: job.phone.trim(),
                location: job.location,
                position: job.position,
                positionType: job.type,
                language,
                website: job.website,
                recaptchaToken: jobToken || undefined,
                recaptchaDeclined: consent !== 'accepted',
            });
            setJobStatus({ type: 'success', name: job.name.trim().split(' ')[0] });
            setJob(EMPTY_JOB);
            setJobConsent(false);
        } catch (error) {
            console.error('Job application submit failed:', error);
            const captchaFailed = error.response?.data?.code === 'CAPTCHA_FAILED';
            setJobStatus({ type: 'error', reason: captchaFailed ? 'captcha' : 'submit' });
        } finally {
            resetCaptcha(jobWidgetId);
            setJobSending(false);
        }
    };

    const handleIdeaSubmit = async (event) => {
        event.preventDefault();
        if (!idea.name.trim() || !idea.email.trim() || !idea.idea.trim() || !idea.capital) {
            setIdeaStatus({ type: 'error', reason: 'required' });
            return;
        }
        if (!EMAIL_RE.test(idea.email.trim())) {
            setIdeaStatus({ type: 'error', reason: 'email' });
            return;
        }
        if (!ideaConsent) {
            setIdeaStatus({ type: 'error', reason: 'consent' });
            return;
        }
        // reCAPTCHA only applies to visitors who accepted optional cookies.
        // Those who declined submit with honeypot + rate-limit protection.
        let ideaToken;
        if (recaptcha.enabled && consent === 'accepted') {
            ideaToken = getCaptchaToken(ideaWidgetId);
            if (ideaToken === '') {
                setIdeaStatus({ type: 'error', reason: 'captcha' });
                return;
            }
        }
        setIdeaSending(true);
        try {
            await axios.post('/api/submissions', {
                type: 'idea',
                name: idea.name.trim(),
                email: idea.email.trim(),
                phone: idea.phone.trim(),
                idea: idea.idea.trim(),
                capital: idea.capital,
                experience: idea.experience.trim(),
                language,
                website: idea.website,
                recaptchaToken: ideaToken || undefined,
                recaptchaDeclined: consent !== 'accepted',
            });
            setIdeaStatus({ type: 'success', name: idea.name.trim().split(' ')[0] });
            setIdea(EMPTY_IDEA);
            setIdeaConsent(false);
        } catch (error) {
            console.error('Idea submit failed:', error);
            const captchaFailed = error.response?.data?.code === 'CAPTCHA_FAILED';
            setIdeaStatus({ type: 'error', reason: captchaFailed ? 'captcha' : 'submit' });
        } finally {
            resetCaptcha(ideaWidgetId);
            setIdeaSending(false);
        }
    };

    const formErrorText = (status) => {
        if (!status || status.type !== 'error') return null;
        if (status.reason === 'email') return ct('public.forms.emailInvalid');
        if (status.reason === 'consent') return ct('public.forms.consentRequired');
        if (status.reason === 'captcha') return ct('public.forms.captchaRequired');
        if (status.reason === 'submit') return ct('public.forms.submitError');
        return ct('public.forms.requiredError');
    };

    const navLinks = [
        { id: 'about', label: ct('public.nav.about') },
        { id: 'locations', label: ct('public.nav.locations') },
        { id: 'join-team', label: ct('public.nav.joinTeam') },
        { id: 'entrepreneurs', label: ct('public.nav.entrepreneurs') },
    ];

    return (
        <div className="public-site">
            {isPreview && (
                <div className="cms-preview-banner" role="status">
                    <span>{t('cms.preview.banner')}</span>
                    <button type="button" onClick={() => window.close()}>
                        {t('cms.preview.close')}
                    </button>
                </div>
            )}
            <header className="site-nav">
                <div className="nav-inner">
                    <a href="#top" className="brand-mark" onClick={scrollTo('top')}>
                        <BrandLogo alt={ct('public.brand.logoAlt')} />
                    </a>
                    <nav className={`links${navOpen ? ' open' : ''}`}>
                        {navLinks.map((link) => (
                            <a key={link.id} href={`#${link.id}`} onClick={scrollTo(link.id)}>
                                {link.label}
                            </a>
                        ))}
                    </nav>
                    <div className="nav-actions">
                        <LanguageSwitcher />
                        <Link to="/login" className="login-pill">{ct('public.nav.login')}</Link>
                        <button
                            type="button"
                            className="nav-toggle"
                            aria-label={ct('public.nav.toggleMenu')}
                            aria-expanded={navOpen}
                            onClick={() => setNavOpen((open) => !open)}
                        >
                            &#9776;
                        </button>
                    </div>
                </div>
            </header>

            <section className="hero" id="top">
                <div className="wrap hero-inner">
                    <span className="eyebrow">{ct('public.hero.eyebrow')}</span>
                    <h1>
                        {ct('public.hero.titleLine1')}
                        <br />
                        {ct('public.hero.titleLine2')} <em>{ct('public.hero.titleEmphasis')}</em>
                    </h1>
                    <p className="lead">{ct('public.hero.lead')}</p>
                    <div className="hero-stats">
                        {[1, 2, 3].map((n) => (
                            <div className="stat" key={n}>
                                <b>{ct(`public.hero.stat${n}Value`)}</b>
                                <span>{ct(`public.hero.stat${n}Label`)}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <svg className="scallop" viewBox="0 0 1200 44" preserveAspectRatio="none" aria-hidden="true">
                    <path d={SCALLOP_PATH} fill="#fffaf3" />
                </svg>
            </section>

            <section className="about" id="about">
                <div className="wrap about-grid">
                    <div className="about-copy">
                        <span className="eyebrow">{ct('public.about.eyebrow')}</span>
                        <h2>{ct('public.about.heading')}</h2>
                        <p>{ct('public.about.p1')}</p>
                        <p>{ct('public.about.p2')}</p>
                        <p>{ct('public.about.p3')}</p>
                    </div>
                    <div className="stat-grid">
                        {[1, 2, 3].map((n) => (
                            <div className="stat-card" key={n}>
                                <div className="cap"><span>{ct(`public.about.card${n}Cap`)}</span></div>
                                <div>
                                    <b>{ct(`public.about.card${n}Title`)}</b>
                                    <br />
                                    <small>{ct(`public.about.card${n}Sub`)}</small>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="locations" id="locations">
                <div className="wrap">
                    <div className="section-head">
                        <span className="eyebrow">{ct('public.locations.eyebrow')}</span>
                        <h2>{ct('public.locations.heading')}</h2>
                        <p>{ct('public.locations.lead')}</p>
                    </div>
                    <div className="filter-row">
                        <button
                            type="button"
                            className={`filter-btn${filter === 'all' ? ' active' : ''}`}
                            onClick={() => setFilter('all')}
                        >
                            {stripCount(ct('public.locations.filterAll'))} ({locations.length})
                        </button>
                        <button
                            type="button"
                            className={`filter-btn${filter === 'aw' ? ' active' : ''}`}
                            onClick={() => setFilter('aw')}
                        >
                            {stripCount(ct('public.locations.filterAw'))} ({brandCount('aw')})
                        </button>
                        <button
                            type="button"
                            className={`filter-btn${filter === 'calienteh' ? ' active' : ''}`}
                            onClick={() => setFilter('calienteh')}
                        >
                            {stripCount(ct('public.locations.filterCalienteh'))} ({brandCount('calienteh')})
                        </button>
                    </div>
                    <div className="loc-grid">
                        {locations.map((loc, index) => {
                            if (filter !== 'all' && loc.brand !== filter) return null;
                            const details = language === 'en'
                                ? (loc.detailsEn || loc.detailsFr)
                                : (loc.detailsFr || loc.detailsEn);
                            return (
                                <div className="loc-card" key={loc.id || loc.name}>
                                    <div className={`loc-photo ${loc.brand}`}>
                                        {loc.photo && (() => {
                                            const rel = loc.photo.startsWith('/') ? loc.photo : `/${loc.photo}`;
                                            // Responsive variants (thumb_/card_) are only produced by the
                                            // upload pipeline for .webp images. For those, hand the browser
                                            // a srcset so a ~350px card downloads the ~800px "card" variant
                                            // instead of the full ≤1920px image; the full is fetched only on
                                            // large or high-DPR screens. Legacy non-webp photos have no
                                            // variants, so they fall back to a plain full-image src.
                                            if (!/\.webp$/i.test(rel)) {
                                                return (
                                                    <img className="loc-img" src={rel} alt={loc.name} loading="lazy" />
                                                );
                                            }
                                            const slash = rel.lastIndexOf('/');
                                            const dir = rel.slice(0, slash + 1);
                                            const file = rel.slice(slash + 1);
                                            const srcSet = `${dir}thumb_${file} 300w, ${dir}card_${file} 800w, ${rel} 1600w`;
                                            return (
                                                <img
                                                    className="loc-img"
                                                    src={rel}
                                                    srcSet={srcSet}
                                                    sizes="(max-width: 620px) 92vw, (max-width: 920px) 45vw, 360px"
                                                    alt={loc.name}
                                                    loading="lazy"
                                                />
                                            );
                                        })()}
                                        <span className="tag">{loc.brand === 'aw' ? 'A&W' : 'Calient-eh'}</span>
                                        {!loc.photo && (loc.brand === 'aw' ? <AwIcon /> : <ChiliIcon />)}
                                        {!loc.photo && (
                                            <span className="placeholder-note">{ct('public.locations.photoComingSoon')}</span>
                                        )}
                                    </div>
                                    <div className="loc-body">
                                        <div className="loc-num"><span>{String(index + 1).padStart(2, '0')}</span></div>
                                        <div className="loc-text">
                                            <b>{loc.name}</b>
                                            {loc.mapsUrl ? (
                                                <a className="loc-addr-link" href={loc.mapsUrl} target="_blank" rel="noopener noreferrer">
                                                    {loc.addr}
                                                </a>
                                            ) : (
                                                <span>{loc.addr}</span>
                                            )}
                                            {details && <small className="loc-details">{details}</small>}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="form-section" id="join-team">
                <div className="wrap form-shell">
                    <div className="form-side">
                        <span className="eyebrow">{ct('public.joinTeam.eyebrow')}</span>
                        <h2>{ct('public.joinTeam.heading')}</h2>
                        <p>{ct('public.joinTeam.lead')}</p>
                        <ul className="perk-list">
                            {[1, 2, 3].map((n) => (
                                <li key={n}><span className="dot" />{ct(`public.joinTeam.perk${n}`)}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="card-form">
                        <form onSubmit={handleJobSubmit} noValidate>
                            <div className="field">
                                <label htmlFor="jobName">{ct('public.joinTeam.form.name')}</label>
                                <input type="text" id="jobName" value={job.name} onChange={handleJobChange('name')} required />
                            </div>
                            <div className="field">
                                <label htmlFor="jobEmail">{ct('public.joinTeam.form.email')}</label>
                                <input type="email" id="jobEmail" value={job.email} onChange={handleJobChange('email')} required />
                            </div>
                            <div className="field">
                                <label htmlFor="jobPhone">{ct('public.joinTeam.form.phone')}</label>
                                <input type="tel" id="jobPhone" value={job.phone} onChange={handleJobChange('phone')} />
                            </div>
                            <div className="hp-field" aria-hidden="true">
                                <label htmlFor="jobWebsite">Website</label>
                                <input type="text" id="jobWebsite" tabIndex="-1" autoComplete="off" value={job.website} onChange={handleJobChange('website')} />
                            </div>
                            <div className="field">
                                <label htmlFor="jobLocation">{ct('public.joinTeam.form.location')}</label>
                                <select id="jobLocation" value={job.location} onChange={handleJobChange('location')} required>
                                    <option value="">{ct('public.joinTeam.form.locationPlaceholder')}</option>
                                    {locations.map((loc) => (
                                        <option key={loc.id || loc.name} value={loc.name}>{loc.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="field">
                                <label htmlFor="jobPosition">{ct('public.joinTeam.form.position')}</label>
                                <select id="jobPosition" value={job.position} onChange={handleJobChange('position')} required>
                                    <option value="">{ct('public.joinTeam.form.positionPlaceholder')}</option>
                                    {POSITION_KEYS.map((key) => (
                                        <option key={key} value={key}>{ct(`public.joinTeam.form.positions.${key}`)}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="field">
                                <label>{ct('public.joinTeam.form.type')}</label>
                                <div className="radio-row">
                                    <label className="radio-opt">
                                        <input
                                            type="radio"
                                            name="jobType"
                                            value="fullTime"
                                            checked={job.type === 'fullTime'}
                                            onChange={handleJobChange('type')}
                                        />
                                        {ct('public.joinTeam.form.fullTime')}
                                    </label>
                                    <label className="radio-opt">
                                        <input
                                            type="radio"
                                            name="jobType"
                                            value="partTime"
                                            checked={job.type === 'partTime'}
                                            onChange={handleJobChange('type')}
                                        />
                                        {ct('public.joinTeam.form.partTime')}
                                    </label>
                                </div>
                            </div>
                            {recaptcha.enabled && consent === 'accepted' && (
                                <div className="field captcha-field">
                                    <div ref={jobCaptchaRef} />
                                </div>
                            )}
                            <label className="consent-check">
                                <input
                                    type="checkbox"
                                    checked={jobConsent}
                                    onChange={(event) => setJobConsent(event.target.checked)}
                                    required
                                />
                                <span>
                                    {t('public.forms.consentLabel')}{' '}
                                    <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                                        {t('public.forms.consentLink')}
                                    </Link>
                                    {t('public.forms.consentLabelEnd')}
                                </span>
                            </label>
                            <button type="submit" className="submit-btn" disabled={jobSending}>
                                {jobSending ? ct('public.forms.sending') : ct('public.joinTeam.form.submit')}
                            </button>
                            {jobStatus && jobStatus.type === 'success' && (
                                <div className="confirm-msg" role="status">
                                    {t('public.joinTeam.form.confirmation', { name: jobStatus.name })}
                                </div>
                            )}
                            {jobStatus && jobStatus.type === 'error' && (
                                <div className="confirm-msg error" role="alert">
                                    {formErrorText(jobStatus)}
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </section>

            <section className="form-section alt" id="entrepreneurs">
                <div className="wrap form-shell">
                    <div className="form-side">
                        <span className="eyebrow">{ct('public.entrepreneurs.eyebrow')}</span>
                        <h2>{ct('public.entrepreneurs.heading')}</h2>
                        <p>{ct('public.entrepreneurs.lead')}</p>
                        <ul className="perk-list">
                            {[1, 2, 3].map((n) => (
                                <li key={n}><span className="dot" />{ct(`public.entrepreneurs.perk${n}`)}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="card-form">
                        <form onSubmit={handleIdeaSubmit} noValidate>
                            <div className="field">
                                <label htmlFor="ideaName">{ct('public.entrepreneurs.form.name')}</label>
                                <input type="text" id="ideaName" value={idea.name} onChange={handleIdeaChange('name')} required />
                            </div>
                            <div className="field">
                                <label htmlFor="ideaEmail">{ct('public.entrepreneurs.form.email')}</label>
                                <input type="email" id="ideaEmail" value={idea.email} onChange={handleIdeaChange('email')} required />
                            </div>
                            <div className="field">
                                <label htmlFor="ideaPhone">{ct('public.entrepreneurs.form.phone')}</label>
                                <input type="tel" id="ideaPhone" value={idea.phone} onChange={handleIdeaChange('phone')} />
                            </div>
                            <div className="hp-field" aria-hidden="true">
                                <label htmlFor="ideaWebsite">Website</label>
                                <input type="text" id="ideaWebsite" tabIndex="-1" autoComplete="off" value={idea.website} onChange={handleIdeaChange('website')} />
                            </div>
                            <div className="field">
                                <label htmlFor="ideaDesc">{ct('public.entrepreneurs.form.idea')}</label>
                                <textarea
                                    id="ideaDesc"
                                    value={idea.idea}
                                    onChange={handleIdeaChange('idea')}
                                    placeholder={ct('public.entrepreneurs.form.ideaPlaceholder')}
                                    required
                                />
                            </div>
                            <div className="field">
                                <label htmlFor="ideaCapital">{ct('public.entrepreneurs.form.capital')}</label>
                                <select id="ideaCapital" value={idea.capital} onChange={handleIdeaChange('capital')} required>
                                    <option value="">{ct('public.entrepreneurs.form.capitalPlaceholder')}</option>
                                    {CAPITAL_KEYS.map((key) => (
                                        <option key={key} value={key}>{ct(`public.entrepreneurs.form.capitalRanges.${key}`)}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="field">
                                <label htmlFor="ideaExp">{ct('public.entrepreneurs.form.experience')}</label>
                                <textarea
                                    id="ideaExp"
                                    value={idea.experience}
                                    onChange={handleIdeaChange('experience')}
                                    placeholder={ct('public.entrepreneurs.form.experiencePlaceholder')}
                                />
                            </div>
                            {recaptcha.enabled && consent === 'accepted' && (
                                <div className="field captcha-field">
                                    <div ref={ideaCaptchaRef} />
                                </div>
                            )}
                            <label className="consent-check">
                                <input
                                    type="checkbox"
                                    checked={ideaConsent}
                                    onChange={(event) => setIdeaConsent(event.target.checked)}
                                    required
                                />
                                <span>
                                    {t('public.forms.consentLabel')}{' '}
                                    <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                                        {t('public.forms.consentLink')}
                                    </Link>
                                    {t('public.forms.consentLabelEnd')}
                                </span>
                            </label>
                            <button type="submit" className="submit-btn" disabled={ideaSending}>
                                {ideaSending ? ct('public.forms.sending') : ct('public.entrepreneurs.form.submit')}
                            </button>
                            {ideaStatus && ideaStatus.type === 'success' && (
                                <div className="confirm-msg" role="status">
                                    {t('public.entrepreneurs.form.confirmation', { name: ideaStatus.name })}
                                </div>
                            )}
                            {ideaStatus && ideaStatus.type === 'error' && (
                                <div className="confirm-msg error" role="alert">
                                    {formErrorText(ideaStatus)}
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </section>

            <footer>
                <div className="wrap">
                    <div className="footer-top">
                        <div className="footer-brand">
                            <div className="brand-mark">
                                <BrandLogo alt={ct('public.brand.logoAlt')} />
                            </div>
                            <p>{ct('public.footer.blurb')}</p>
                        </div>
                        <div className="footer-cols">
                            <div className="footer-col">
                                <b>{ct('public.footer.explore')}</b>
                                <a href="#about" onClick={scrollTo('about')}>{ct('public.footer.aboutUs')}</a>
                                <a href="#locations" onClick={scrollTo('locations')}>{ct('public.footer.ourLocations')}</a>
                                <a href="#join-team" onClick={scrollTo('join-team')}>{ct('public.nav.joinTeam')}</a>
                                <a href="#entrepreneurs" onClick={scrollTo('entrepreneurs')}>{ct('public.nav.entrepreneurs')}</a>
                            </div>
                            <div className="footer-col">
                                <b>{ct('public.footer.brands')}</b>
                                <a href="#locations" onClick={showBrand('aw')}>{ct('public.footer.awRestaurants')}</a>
                                <a href="#locations" onClick={showBrand('calienteh')}>{ct('public.footer.calienteh')}</a>
                            </div>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <span>{ct('public.footer.copyright')}</span>
                        <div className="footer-bottom-links">
                            <Link to="/privacy" className="staff-link">{ct('public.footer.privacy')}</Link>
                            <button type="button" className="cookie-settings-btn" onClick={openCookieSettings}>
                                {ct('public.footer.cookieSettings')}
                            </button>
                            <Link to="/login" className="staff-link">{ct('public.footer.staffLogin')}</Link>
                        </div>
                    </div>
                </div>
            </footer>

            {bannerOpen && (
                <CookieConsent
                    onAccept={acceptCookies}
                    onDecline={declineCookies}
                    onDismiss={dismissBanner}
                />
            )}
        </div>
    );
};

export default PublicSite;
